import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { createSocketConnection, authenticateSocket } from '../../utils/socketConfig';
import { EditIcon, DeleteIcon, ClearIcon } from './components/Icons';

const API_BASE_URL = import.meta.env.VITE_API_URL;

// Additional Icons
const RefreshIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
  </svg>
);

const ArchiveIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
    <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
  </svg>
);

const UnarchiveIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
    <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
  </svg>
);

const InboxIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm-1 9v-1h5v2H5a1 1 0 01-1-1zm7 1h4a1 1 0 001-1v-1h-5v2zm0-4h5V8h-5v2zM9 8H4v2h5V8z" clipRule="evenodd" />
  </svg>
);

const SearchOffIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
    <path fillRule="evenodd" d="M12.293 11.707l1.414-1.414L13.707 9.879 12.293 11.293zm-4.586-4.586l1.414-1.414L9.121 5.293 7.707 6.707z" clipRule="evenodd" />
  </svg>
);

// Constants
const STATUS_STYLES = {
  PENDING: 'bg-orange-50 text-orange-600 border-orange-200',
  IN_PROGRESS: 'bg-blue-50 text-blue-600 border-blue-200',
  RESOLVED: 'bg-green-50 text-green-600 border-green-200',
  CLOSED: 'bg-gray-50 text-gray-600 border-gray-200'
};

const PRIORITY_STYLES = {
  HIGH: 'bg-red-50 text-red-600 border-red-200 font-semibold',
  MEDIUM: 'bg-orange-50 text-orange-600 border-orange-200',
  LOW: 'bg-green-50 text-green-600 border-green-200'
};

const OVERDUE_LEVELS = {
  URGENT: { hours: 48, level: 2, label: 'URGENT', classes: 'bg-red-50 text-red-600 border-red-200 animate-pulse', icon: '🚨' },
  OVERDUE: { hours: 24, level: 1, label: 'OVERDUE', classes: 'bg-orange-50 text-orange-600 border-orange-200', icon: '⚠️' }
};

// Helper Functions
const formatDate = (dateString) => new Date(dateString).toLocaleString();

const getOverdueLevel = (ticket) => {
  if (ticket.status !== 'PENDING') return 0;
  const now = new Date();
  const createdDate = new Date(ticket.createdAt);
  const updatedDate = new Date(ticket.updatedAt || ticket.createdAt);
  const lastActionDate = updatedDate > createdDate ? updatedDate : createdDate;
  const hoursSinceLastAction = (now - lastActionDate) / (1000 * 60 * 60);
  if (hoursSinceLastAction > OVERDUE_LEVELS.URGENT.hours) return 2;
  if (hoursSinceLastAction > OVERDUE_LEVELS.OVERDUE.hours) return 1;
  return 0;
};

const getOverdueStyle = (ticket) => {
  const level = getOverdueLevel(ticket);
  const styles = {
    2: 'border-l-4 border-red-500 bg-red-50 hover:bg-red-100',
    1: 'border-l-4 border-orange-500 bg-orange-50 hover:bg-orange-100'
  };
  return styles[level] || '';
};

const shouldRemoveFromTab = (tabValue, status) => {
  if (tabValue === 0) return status === 'RESOLVED' || status === 'CLOSED';
  return status !== 'RESOLVED' && status !== 'CLOSED';
};

const shouldAddToTab = (tabValue, status) => {
  if (tabValue === 0) return status !== 'RESOLVED' && status !== 'CLOSED';
  return status === 'RESOLVED' || status === 'CLOSED';
};

// Components
const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index} id={`tickets-tabpanel-${index}`} aria-labelledby={`tickets-tab-${index}`}>
    {value === index && <div className="py-6">{children}</div>}
  </div>
);

const StatusDialog = ({ open, ticket, newStatus, onClose, onUpdate, onChange }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4 font-poppins text-blue-600">Update Ticket Status</h3>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2 font-poppins">New Status</label>
            <select
              value={newStatus}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins"
            >
              <option value="PENDING">Pending</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="RESOLVED">Resolved</option>
              <option value="CLOSED">Closed</option>
            </select>
          </div>
          <div className="flex space-x-3">
            <button onClick={onClose} className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-poppins">
              Cancel
            </button>
            <button onClick={() => onUpdate(ticket, newStatus)} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-poppins">
              Update Status
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeleteDialog = ({ open, ticket, onClose, onConfirm }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4 font-poppins text-red-600">Confirm Ticket Deletion</h3>
          <p className="text-gray-600 mb-6 font-poppins">
            Are you sure you want to delete ticket #{ticket?.id} ({ticket?.trackingId})? This action cannot be undone.
          </p>
          <div className="flex space-x-3">
            <button onClick={onClose} className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-poppins">
              Cancel
            </button>
            <button onClick={onConfirm} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-poppins">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Notification = ({ open, message, severity, onClose }) => {
  if (!open) return null;
  const colors = {
    success: 'bg-green-500 text-white',
    error: 'bg-red-500 text-white',
    warning: 'bg-yellow-500 text-white',
    info: 'bg-blue-500 text-white'
  };
  return (
    <div className="fixed bottom-4 right-4 z-50 animate-fade-in">
      <div className={`px-6 py-4 rounded-lg shadow-lg min-w-[300px] max-w-md font-poppins ${colors[severity] || colors.info}`}>
        <div className="flex items-center justify-between gap-4">
          <span className="font-medium">{message}</span>
          <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors duration-200">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const ManageTickets = () => {
  const navigate = useNavigate();
  
  const [state, setState] = useState({
    tickets: [],
    loading: true,
    error: null,
    page: 0,
    rowsPerPage: 10,
    totalTickets: 0,
    categoryFilter: '',
    searchQuery: '',
    tabValue: 0,
    socket: null
  });

  const [dialogs, setDialogs] = useState({
    status: { open: false, ticket: null, newStatus: '' },
    delete: { open: false, ticket: null }
  });
  
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const updateTicketsState = (updater) => {
    setState(prevState => {
      if (typeof updater === 'function') {
        const result = updater(prevState);
        return { ...prevState, ...result };
      }
      return { ...prevState, ...updater };
    });
  };

  const updateTicketInList = (ticketId, updates) => {
    updateTicketsState(prevState => ({
      tickets: prevState.tickets.map(t => t.id === ticketId ? { ...t, ...updates } : t)
    }));
  };

  const removeTicketFromList = (ticketId) => {
    updateTicketsState(prevState => ({
      tickets: prevState.tickets.filter(t => t.id !== ticketId),
      totalTickets: Math.max(0, prevState.totalTickets - 1)
    }));
  };

  const addTicketToList = (ticket) => {
    updateTicketsState(prevState => ({
      tickets: [ticket, ...prevState.tickets],
      totalTickets: prevState.totalTickets + 1
    }));
  };

  const handleSocketTicketUpdate = (updatedTicket) => {
    updateTicketsState(prevState => {
      const { tickets, tabValue, categoryFilter } = prevState;
      const ticketIndex = tickets.findIndex(t => t.id === updatedTicket.id);
      const newTickets = [...tickets];
    
      if (ticketIndex !== -1) {
        const shouldRemove = shouldRemoveFromTab(tabValue, updatedTicket.status);
        if (shouldRemove) {
          newTickets.splice(ticketIndex, 1);
          return { tickets: newTickets, totalTickets: Math.max(0, prevState.totalTickets - 1) };
        }
        
        const matchesCategory = !categoryFilter || updatedTicket.category === categoryFilter;
        if (matchesCategory) {
          newTickets[ticketIndex] = updatedTicket;
          return { tickets: newTickets };
        }
        
        newTickets.splice(ticketIndex, 1);
        return { tickets: newTickets, totalTickets: Math.max(0, prevState.totalTickets - 1) };
      }
      
      const shouldAdd = shouldAddToTab(tabValue, updatedTicket.status);
      const matchesCategory = !categoryFilter || updatedTicket.category === categoryFilter;
      if (shouldAdd && matchesCategory) {
        return { 
          tickets: [updatedTicket, ...tickets], 
          totalTickets: prevState.totalTickets + 1 
        };
      }
      
      return prevState;
    });
    showNotification(`Ticket #${updatedTicket.id} was updated to ${updatedTicket.status.replace('_', ' ').toLowerCase()}`, 'info');
  };

  const handleSocketNewTicket = (newTicket) => {
    updateTicketsState(prevState => {
      const { tickets, tabValue, categoryFilter } = prevState;
      const shouldAdd = tabValue === 0 && shouldAddToTab(tabValue, newTicket.status);
      const matchesCategory = !categoryFilter || newTicket.category === categoryFilter;
      const ticketExists = tickets.some(t => t.id === newTicket.id);
      
      if (shouldAdd && matchesCategory && !ticketExists) {
        showNotification(`New ticket #${newTicket.id} has been created`, 'success');
        return { 
          tickets: [newTicket, ...tickets], 
          totalTickets: prevState.totalTickets + 1 
        };
      }
      return prevState;
    });
  };

  useEffect(() => {
    const newSocket = createSocketConnection();
    authenticateSocket(newSocket);

    const socketEvents = {
      connect: () => console.log('Socket connected successfully', newSocket.id),
      authenticated: (response) => {
        if (!response.success) {
          updateTicketsState({ error: `Socket authentication failed: ${response.message}` });
          showNotification(`Real-time updates not available: ${response.message}`, 'warning');
        }
      },
      connect_error: (error) => {
        console.error('Socket connection error:', error);
        updateTicketsState({ error: 'Failed to connect to notification service' });
      },
      disconnect: (reason) => {
        if (reason === 'io server disconnect') newSocket.connect();
      },
      ticketUpdated: handleSocketTicketUpdate,
      newTicket: handleSocketNewTicket,
      ticketDeleted: (data) => {
        if (data?.id) {
          removeTicketFromList(data.id);
          showNotification(`Ticket #${data.id} was deleted successfully`, 'info');
        }
      }
    };

    Object.entries(socketEvents).forEach(([event, handler]) => {
      newSocket.on(event, handler);
    });

    updateTicketsState({ socket: newSocket });

    return () => {
      newSocket?.disconnect();
    };
  }, [state.tabValue, state.categoryFilter]);

  const fetchTickets = useCallback(async () => {
    try {
      updateTicketsState({ loading: true });
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const params = new URLSearchParams({
        page: state.page + 1,
        limit: state.rowsPerPage,
        ...(state.categoryFilter && { category: state.categoryFilter }),
        ...(state.searchQuery && { search: state.searchQuery })
      });
      
      if (state.tabValue === 0) {
        params.append('excludeResolved', 'true');
      } else {
        params.append('status', 'RESOLVED');
        params.append('status', 'CLOSED');
      }

      const response = await axios.get(`${API_BASE_URL}/admin/tickets?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        updateTicketsState({
          tickets: response.data.data.tickets,
          totalTickets: response.data.data.pagination.total,
          error: null
        });
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load tickets';
      updateTicketsState({ error: errorMessage });
      showNotification(errorMessage, 'error');
    } finally {
      updateTicketsState({ loading: false });
    }
  }, [state.page, state.rowsPerPage, state.categoryFilter, state.searchQuery, state.tabValue]);

  const handleStatusUpdate = async (ticket, newStatus) => {
    try {
      updateTicketsState({ loading: true });
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/admin/tickets/${ticket.id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        updateTicketsState(prevState => {
          const shouldRemove = shouldRemoveFromTab(prevState.tabValue, newStatus);
          if (shouldRemove) {
            return { tickets: prevState.tickets.filter(t => t.id !== ticket.id) };
          }
          return { tickets: prevState.tickets.map(t => t.id === ticket.id ? { ...t, status: newStatus } : t) };
        });
        
        setDialogs(prev => ({ ...prev, status: { open: false, ticket: null, newStatus: '' } }));
        showNotification(`Ticket status updated to ${newStatus.toLowerCase().replace('_', ' ')}`, 'success');
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update ticket status';
      updateTicketsState({ error: errorMessage });
      showNotification(errorMessage, 'error');
    } finally {
      updateTicketsState({ loading: false });
    }
  };

  const handleDeleteTicket = async () => {
    if (!dialogs.delete.ticket) return;
    
    try {
      updateTicketsState({ loading: true });
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_BASE_URL}/admin/tickets/${dialogs.delete.ticket.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const ticketId = dialogs.delete.ticket.id;
        removeTicketFromList(ticketId);
        setDialogs(prev => ({ ...prev, delete: { open: false, ticket: null } }));
        showNotification(`Ticket #${ticketId} was deleted successfully`, 'success');
        
        if (state.socket) {
          state.socket.emit('manualDeleteNotification', { id: ticketId });
        }
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to delete ticket';
      updateTicketsState({ error: errorMessage });
      showNotification(errorMessage, 'error');
      setDialogs(prev => ({ ...prev, delete: { open: false, ticket: null } }));
    } finally {
      updateTicketsState({ loading: false });
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(fetchTickets, 500);
    return () => clearTimeout(debounceTimer);
  }, [state.page, state.rowsPerPage, state.categoryFilter, state.searchQuery, state.tabValue, fetchTickets]);

  const sortedTickets = [...state.tickets].sort((a, b) => {
    const overdueA = getOverdueLevel(a);
    const overdueB = getOverdueLevel(b);
    if (overdueA !== overdueB) return overdueB - overdueA;
    const priorityRanking = { HIGH: 1, MEDIUM: 2, LOW: 3 };
    return (priorityRanking[a.priority] || 4) - (priorityRanking[b.priority] || 4);
  });

  const renderOverdueBadge = (ticket) => {
    const level = getOverdueLevel(ticket);
    if (level === 0) return null;
    const config = level === 2 ? OVERDUE_LEVELS.URGENT : OVERDUE_LEVELS.OVERDUE;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border ${config.classes}`}>
        {config.icon} {config.label}
      </span>
    );
  };

  const renderActionButtons = (ticket, isMobile = false) => {
    const buttonClass = isMobile ? "text-green-600 hover:text-green-900 p-2 rounded-lg hover:bg-green-50 transition-colors ml-2" : "text-green-600 hover:text-green-900 p-2 rounded-lg hover:bg-green-50 transition-colors";
    const deleteButtonClass = isMobile ? "text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50 transition-colors ml-2" : "text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50 transition-colors";
    
    return (
      <>
        {state.tabValue === 0 && isMobile && (
          <button
            onClick={() => setDialogs(prev => ({ ...prev, status: { open: true, ticket, newStatus: ticket.status } }))}
            className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50 transition-colors"
            title="Update Status"
          >
            <EditIcon className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={() => handleStatusUpdate(ticket, state.tabValue === 1 ? 'IN_PROGRESS' : 'RESOLVED')}
          className={buttonClass}
          title={state.tabValue === 1 ? "Restore Ticket" : "Mark as Resolved"}
        >
          {state.tabValue === 1 ? <UnarchiveIcon className="w-4 h-4" /> : <ArchiveIcon className="w-4 h-4" />}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setDialogs(prev => ({ ...prev, delete: { open: true, ticket } }));
          }}
          className={deleteButtonClass}
          title="Delete Ticket"
        >
          <DeleteIcon className="w-4 h-4" />
        </button>
      </>
    );
  };

  const renderFilters = () => (
    <div className="mb-6 flex flex-col sm:flex-row gap-4 flex-wrap bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 relative z-10">
      <div className="flex-1 sm:min-w-[200px]">
        <label htmlFor="category-filter" className="block text-sm font-medium text-gray-700 mb-2 font-poppins">Category</label>
        <select
          id="category-filter"
          value={state.categoryFilter}
          onChange={(e) => updateTicketsState({ categoryFilter: e.target.value, page: 0 })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins cursor-pointer"
        >
          <option value="">All Categories</option>
          <option value="TROUBLESHOOTING">Troubleshooting</option>
          <option value="ACCOUNT_MANAGEMENT">Account Management</option>
          <option value="DOCUMENT_UPLOAD">Document Upload</option>
          <option value="TECHNICAL_ASSISTANCE">Technical Assistance</option>
        </select>
      </div>
      <div className="flex-1 sm:min-w-[300px]">
        <label htmlFor="search-input" className="block text-sm font-medium text-gray-700 mb-2 font-poppins">Search</label>
        <input
          id="search-input"
          type="text"
          value={state.searchQuery}
          onChange={(e) => updateTicketsState({ searchQuery: e.target.value, page: 0 })}
          placeholder="Search by subject, message, or user..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins"
        />
      </div>
      {(state.categoryFilter || state.searchQuery) && (
        <div className="flex items-end">
          <button
            onClick={() => updateTicketsState({ categoryFilter: '', searchQuery: '', page: 0 })}
            className="flex items-center px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 hover:border-red-400 transition-colors font-poppins text-sm font-medium"
          >
            <ClearIcon className="w-4 h-4 mr-2" />
            Clear All
          </button>
        </div>
      )}
    </div>
  );

  const renderEmptyState = () => {
    const isFiltering = state.categoryFilter || state.searchQuery;
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 sm:px-8 bg-white rounded-xl shadow-sm border border-gray-200 my-4">
        {isFiltering ? <SearchOffIcon className="w-16 h-16 sm:w-24 sm:h-24 text-gray-400 mb-4" /> : <InboxIcon className="w-16 h-16 sm:w-24 sm:h-24 text-gray-400 mb-4" />}
        <h3 className="text-lg sm:text-xl font-semibold mb-2 text-gray-700 font-poppins text-center">
          {isFiltering ? 'No matching tickets found' : 'No tickets yet'}
        </h3>
        <p className="text-gray-500 mb-6 max-w-md text-center font-poppins text-sm sm:text-base">
          {isFiltering 
            ? 'Try adjusting your search criteria or clearing your filters to see more results.' 
            : state.tabValue === 0 
              ? 'There are currently no active tickets in the system.'
              : 'There are currently no resolved tickets in the system.'
          }
        </p>
        {isFiltering && (
          <button
            onClick={() => updateTicketsState({ categoryFilter: '', searchQuery: '', page: 0 })}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-poppins font-medium"
          >
            Clear all filters
          </button>
        )}
      </div>
    );
  };

  const renderTicketCard = (ticket) => (
    <div 
      key={ticket.id} 
      onClick={() => navigate(`/admin/tickets/${ticket.id}`)}
      className={`mb-4 rounded-lg cursor-pointer shadow-sm border border-gray-200 transition-all duration-200 hover:shadow-md hover:-translate-y-1 p-4 ${getOverdueStyle(ticket)}`}
    >
      <div className="flex justify-between mb-3">
        <div>
          <h4 className="text-sm font-semibold font-poppins text-gray-900">Ticket #{ticket.id}</h4>
          <p className="text-xs font-mono text-blue-600 font-poppins">{ticket.trackingId}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {renderOverdueBadge(ticket)}
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${PRIORITY_STYLES[ticket.priority]}`}>
            {ticket.priority}
          </span>
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${STATUS_STYLES[ticket.status]}`}>
            {ticket.status.replace(/_/g, ' ')}
          </span>
        </div>
      </div>
      <div className="mb-3">
        <p className="text-xs text-gray-500 font-poppins mb-1">Category</p>
        <p className="text-sm font-medium font-poppins text-gray-900">{ticket.category.replace(/_/g, ' ')}</p>
      </div>
      <div className="mb-3">
        <p className="text-xs text-gray-500 font-poppins mb-1">Created</p>
        <p className="text-xs font-poppins text-gray-600">{formatDate(ticket.createdAt)}</p>
      </div>
      <div className="flex justify-end border-t border-dashed border-gray-200 pt-3 mt-3" onClick={(e) => e.stopPropagation()}>
        {renderActionButtons(ticket, true)}
      </div>
    </div>
  );

  const renderTicketsTable = () => (
    <>
      {renderFilters()}
      {state.tickets.length > 0 ? (
        <>
          <div className="block sm:hidden mb-4">{sortedTickets.map(renderTicketCard)}</div>
          <div className="hidden sm:block bg-white rounded-xl shadow-sm border border-gray-200 mb-4 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['ID', 'Tracking ID', 'Category', 'Status', 'Priority', 'Created date', 'Actions'].map((header) => (
                      <th key={header} className="px-4 py-3 text-left text-xs font-semibold text-blue-600 uppercase tracking-wider font-poppins">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedTickets.map((ticket) => (
                    <tr 
                      key={ticket.id}
                      onClick={() => navigate(`/admin/tickets/${ticket.id}`)}
                      className={`cursor-pointer hover:bg-gray-50 transition-colors ${getOverdueStyle(ticket)}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 font-poppins">{ticket.id}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600 font-mono font-poppins">{ticket.trackingId}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 font-poppins">{ticket.category.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${STATUS_STYLES[ticket.status]}`}>
                            {ticket.status.replace(/_/g, ' ')}
                          </span>
                          {renderOverdueBadge(ticket)}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${PRIORITY_STYLES[ticket.priority]}`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-poppins">{formatDate(ticket.createdAt)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-center gap-2">{renderActionButtons(ticket)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm text-gray-700 font-poppins mr-4">Rows per page:</span>
                <select
                  value={state.rowsPerPage}
                  onChange={(e) => updateTicketsState({ rowsPerPage: parseInt(e.target.value, 10), page: 0 })}
                  className="px-3 py-1 border border-gray-300 rounded text-sm font-poppins"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => updateTicketsState({ page: state.page - 1 })}
                  disabled={state.page === 0}
                  className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-poppins"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700 font-poppins">
                  {state.page * state.rowsPerPage + 1}-{Math.min((state.page + 1) * state.rowsPerPage, state.totalTickets)} of {state.totalTickets}
                </span>
                <button
                  onClick={() => updateTicketsState({ page: state.page + 1 })}
                  disabled={state.page >= Math.ceil(state.totalTickets / state.rowsPerPage) - 1}
                  className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-poppins"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        renderEmptyState()
      )}
    </>
  );

  if (state.loading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[400px] p-4 sm:p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="font-poppins mt-4 text-gray-600 text-sm sm:text-base">Loading tickets...</p>
      </div>
    );
  }

  const urgentCount = state.tabValue === 0 && state.tickets.length > 0 ? state.tickets.filter(t => getOverdueLevel(t) === 2).length : 0;
  const overdueCount = state.tabValue === 0 && state.tickets.length > 0 ? state.tickets.filter(t => getOverdueLevel(t) === 1).length : 0;

  return (
    <div className="p-4 sm:p-8 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-poppins bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
            Manage Tickets
          </h1>
          {state.tabValue === 0 && state.tickets.length > 0 && (
            <div className="mt-2 flex gap-2 flex-wrap">
              {urgentCount > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border bg-red-50 text-red-600 border-red-200 animate-pulse">
                  🚨 {urgentCount} Urgent Ticket{urgentCount > 1 ? 's' : ''}
                </span>
              )}
              {overdueCount > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border bg-orange-50 text-orange-600 border-orange-200">
                  ⚠️ {overdueCount} Overdue Ticket{overdueCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
        </div>
        <button
          onClick={fetchTickets} 
          disabled={state.loading}
          className="bg-white shadow-sm rounded-lg p-3 hover:bg-gray-50 transition-colors disabled:opacity-50"
          title="Refresh List"
        >
          <RefreshIcon className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {state.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 font-poppins">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {state.error}
          </div>
        </div>
      )}

      <div className="border-b border-gray-200 mb-6">
        <div className="flex">
          {['Active Tickets', 'Resolved Tickets'].map((label, index) => (
            <button
              key={index}
              onClick={() => updateTicketsState({ tabValue: index, page: 0 })}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors font-poppins ${
                state.tabValue === index
                  ? 'border-blue-600 text-blue-600 font-semibold'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <TabPanel value={state.tabValue} index={0}>{renderTicketsTable()}</TabPanel>
      <TabPanel value={state.tabValue} index={1}>{renderTicketsTable()}</TabPanel>

      <StatusDialog
        open={dialogs.status.open}
        ticket={dialogs.status.ticket}
        newStatus={dialogs.status.newStatus}
        onClose={() => setDialogs(prev => ({ ...prev, status: { open: false, ticket: null, newStatus: '' } }))}
        onUpdate={handleStatusUpdate}
        onChange={(value) => setDialogs(prev => ({ ...prev, status: { ...prev.status, newStatus: value } }))}
      />

      <DeleteDialog
        open={dialogs.delete.open}
        ticket={dialogs.delete.ticket}
        onClose={() => setDialogs(prev => ({ ...prev, delete: { open: false, ticket: null } }))}
        onConfirm={handleDeleteTicket}
      />

      <Notification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification({ ...notification, open: false })}
      />
    </div>
  );
};

export default ManageTickets;
